import React from 'react';
import {
    FaChartBar, FaCalendarAlt, FaDownload, FaCar, FaMoneyBillWave,
    FaUsers, FaEye, FaTimes, FaFileContract
} from 'react-icons/fa';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar
} from 'recharts';
import { getCurrentUser, isAdmin, formatDataForCSV, downloadCSV } from '../services/api';
import { useNavigate } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';

const AdminAnalyticsPage = () => {
    const user = getCurrentUser();
    const navigate = useNavigate();

    // Use the new analytics hook
    const {
        // State
        loading,
        error,
        success,
        kpiData,
        chartData,

        // Filter State  
        dateRange,
        customDateRange,
        tempCustomDateRange,
        showCustomDate,

        // Modal State
        showModal,
        modalLoading,
        modalError,
        detailedModalData,
        modalType,

        // Promissory Note List State
        promissoryNoteListLoading,
        promissoryNoteList,

        // Actions
        handleDateRangeChange,
        handleCustomDateChange,
        applyCustomDateFilter,
        loadPromissoryNoteList,
        exportToExcel,
        openModal,
        closeModal,
        clearMessages,

        // Computed
        isReady
    } = useAnalytics();

    // Senetli Araç Listesi Modal State
    const [showPromissoryNoteModal, setShowPromissoryNoteModal] = React.useState(false);

    // Senetli araç listesi modalını aç
    const openPromissoryNoteModal = async () => {
        setShowPromissoryNoteModal(true);
        await loadPromissoryNoteList();
    };

    // Senetli araç listesi modalını kapat
    const closePromissoryNoteModal = () => {
        setShowPromissoryNoteModal(false);
    };

    // Admin kontrolü
    React.useEffect(() => {
        if (!user || !isAdmin()) {
            navigate('/');
            return;
        }
    }, [user, navigate]);

    // Helper function to safely convert to number
    const safeNumber = (value, defaultValue = 0) => {
        const num = Number(value);
        return isNaN(num) ? defaultValue : num;
    };

    const COLORS = [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
    ];

    // Modal render functions (simplified versions)
    const renderModalContent = () => {
        if (modalLoading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                </div>
            );
        }

        if (modalError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{ color: 'var(--danger-color)', marginBottom: '20px' }}>
                        <strong>⚠️ Hata: {modalError}</strong>
                    </div>
                </div>
            );
        }

        if (!detailedModalData) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Detaylı veri bulunamadı
                </div>
            );
        }

        // Render modal content based on type
        switch (modalType) {
            default:
                return (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
                        <h3>Detaylı Analiz</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>
                            Bu modal için detaylı görünüm hazırlanıyor.
                        </p>
                    </div>
                );
        }
    };



    if (!user || !isAdmin()) {
        return (
            <div className="container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <h2 style={{ color: 'var(--danger-color)', fontSize: 'var(--font-xl)', fontWeight: '700' }}>Erişim Reddedildi</h2>
                    <p style={{ color: 'var(--text-secondary)' }}>Bu sayfaya sadece admin kullanıcıları erişebilir.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: 'var(--gray-50)', padding: 'var(--space-8) 0' }}>
            <div className="container">
                {/* Header */}
                <div style={{ marginBottom: 'var(--space-8)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        <div>
                            <h1 className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                <FaChartBar style={{ color: 'var(--black)' }} />
                                Admin Dashboard
                            </h1>
                            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--font-lg)' }}>
                                Kapsamlı işletme analitikleri ve performans metrikleri
                            </p>
                        </div>

                        {/* Export Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)' }}>
                            <button
                                onClick={exportToExcel}
                                className="btn btn-success"
                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                            >
                                <FaDownload />
                                Excel İndir
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="alert alert-danger" style={{ marginBottom: 'var(--space-6)' }}>
                        {error}
                        <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none' }}>×</button>
                    </div>
                )}

                {success && (
                    <div className="alert alert-success" style={{ marginBottom: 'var(--space-6)' }}>
                        {success}
                        <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none' }}>×</button>
                    </div>
                )}

                {/* Date Range Buttons */}
                <div style={{ marginBottom: 'var(--space-6)' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        {[
                            { value: '30', label: 'Son 30 Gün', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' },
                            { value: '90', label: 'Son 3 Ay', gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
                            { value: '180', label: 'Son 6 Ay', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' },
                            { value: '270', label: 'Son 9 Ay', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
                            { value: '365', label: 'Son 1 Yıl', gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
                            { value: 'custom', label: 'Özel Tarih', gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)' }
                        ].map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleDateRangeChange(option.value)}
                                className="btn"
                                style={{
                                    padding: 'var(--space-3) var(--space-5)',
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '600',
                                    background: dateRange === option.value ? option.gradient : 'var(--white)',
                                    color: dateRange === option.value ? 'var(--white)' : 'var(--text-primary)',
                                    border: dateRange === option.value ? 'none' : '2px solid var(--gray-300)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    transition: 'var(--transition)',
                                    minWidth: '120px',
                                    textAlign: 'center'
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {showCustomDate && (
                        <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
                            <div className="card-body" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <label className="form-label" style={{ fontSize: 'var(--font-xs)' }}>Başlangıç</label>
                                    <input
                                        type="date"
                                        value={tempCustomDateRange.start}
                                        onChange={(e) => handleCustomDateChange('start', e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label className="form-label" style={{ fontSize: 'var(--font-xs)' }}>Bitiş</label>
                                    <input
                                        type="date"
                                        value={tempCustomDateRange.end}
                                        onChange={(e) => handleCustomDateChange('end', e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                                <button
                                    onClick={applyCustomDateFilter}
                                    disabled={!tempCustomDateRange.start || !tempCustomDateRange.end}
                                    className="btn"
                                    style={{
                                        background: tempCustomDateRange.start && tempCustomDateRange.end 
                                            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                                            : 'var(--gray-300)',
                                        color: 'white',
                                        border: 'none',
                                        padding: 'var(--space-2) var(--space-4)',
                                        fontWeight: '600',
                                        cursor: tempCustomDateRange.start && tempCustomDateRange.end ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    Uygula
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* KPI Cards - Using hook data */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 'var(--space-6)',
                    marginBottom: 'var(--space-12)'
                }}>
                    {/* Total Revenue */}
                    <div className="card" style={{
                        background: 'var(--white)',
                        border: '1px solid #e5e7eb',
                        borderLeft: '4px solid #374151',
                        transition: 'var(--transition)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <div className="card-body" style={{ padding: 'var(--space-6)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                                <h3 style={{
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    margin: 0,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Toplam Satış Geliri</h3>
                                <FaMoneyBillWave style={{ fontSize: 'var(--font-xl)', color: '#374151', opacity: '0.7' }} />
                            </div>
                            <p style={{
                                fontSize: 'var(--font-3xl)',
                                fontWeight: '800',
                                color: '#111827',
                                margin: '0 0 var(--space-2) 0',
                                lineHeight: '1.1'
                            }}>₺{(kpiData.totalRevenue || 0).toLocaleString('tr-TR')}</p>
                            <div style={{
                                fontSize: 'var(--font-sm)',
                                color: '#9ca3af',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <FaCar style={{ fontSize: 'var(--font-xs)' }} />
                                {kpiData.totalVehiclesSold} araç satıldı
                            </div>
                        </div>
                    </div>

                    {/* Net Profit */}
                    <div className="card" style={{
                        background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
                        color: '#b91c1c',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                    }}>
                        <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <h3 style={{
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '500',
                                    opacity: '0.9',
                                    marginBottom: 'var(--space-2)',
                                    color: '#b91c1c',
                                    cursor: 'help'
                                }}
                                    title="Net Kar = Toplam Satış - Toplam Alış - Giderler - Sigorta Maliyeti + Sigorta İadesi"
                                >
                                    Net Kar
                                </h3>
                                <p style={{ fontSize: 'var(--font-2xl)', fontWeight: '700', marginBottom: 'var(--space-1)', color: '#b91c1c' }}>
                                    ₺{Math.round(kpiData.totalProfit || 0).toLocaleString('tr-TR')}
                                </p>
                                <div style={{ fontSize: 'var(--font-xs)', opacity: '0.8', color: '#b91c1c' }}>
                                    %{kpiData.profitMargin || 0} kar marjı
                                </div>
                                <div style={{
                                    fontSize: 'var(--font-xs)',
                                    opacity: '0.7',
                                    color: '#b91c1c',
                                    marginTop: 'var(--space-1)',
                                    lineHeight: '1.2'
                                }}>
                                    Satış - Alış - Gider - Sigorta + İade
                                </div>
                            </div>
                            <FaMoneyBillWave style={{ fontSize: 'var(--font-3xl)', opacity: '0.6', color: '#b91c1c' }} />
                        </div>
                    </div>
                </div>

                {/* Senetli Satışlar KPI Card - Seçenek B: Tek büyük kart, 3 metrik */}
                <div style={{ marginBottom: 'var(--space-12)' }}>
                    <div className="card" style={{
                        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                        border: 'none',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden'
                    }}>
                        {/* Card Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            padding: 'var(--space-4) var(--space-6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                <FaFileContract style={{ fontSize: 'var(--font-xl)', color: 'white' }} />
                                <h3 style={{
                                    fontSize: 'var(--font-lg)',
                                    fontWeight: '700',
                                    color: 'white',
                                    margin: 0
                                }}>Senetli Satışlar</h3>
                            </div>
                            <button
                                onClick={openPromissoryNoteModal}
                                title="Senetli araç listesini görüntüle"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-lg)',
                                    padding: 'var(--space-2)',
                                    borderRadius: 'var(--radius)',
                                    transition: 'var(--transition)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaDownload />
                            </button>
                        </div>

                        {/* Card Body - 3 Columns */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '1px',
                            background: '#93c5fd'
                        }}>
                            {/* Satış Adedi */}
                            <div style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                padding: 'var(--space-6)',
                                textAlign: 'center'
                            }}>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '600',
                                    color: '#1e40af',
                                    margin: '0 0 var(--space-2) 0',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Satış Adedi</p>
                                <p style={{
                                    fontSize: 'var(--font-3xl)',
                                    fontWeight: '800',
                                    color: '#1e3a8a',
                                    margin: '0 0 var(--space-1) 0',
                                    lineHeight: '1.1'
                                }}>{(kpiData.promissoryNoteCount || 0).toLocaleString('tr-TR')}</p>
                                <p style={{
                                    fontSize: 'var(--font-xs)',
                                    color: '#3b82f6',
                                    margin: 0
                                }}>adet senet</p>
                            </div>

                            {/* Ana Para */}
                            <div style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                padding: 'var(--space-6)',
                                textAlign: 'center'
                            }}>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '600',
                                    color: '#1e40af',
                                    margin: '0 0 var(--space-2) 0',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Ana Para</p>
                                <p style={{
                                    fontSize: 'var(--font-3xl)',
                                    fontWeight: '800',
                                    color: '#1e3a8a',
                                    margin: '0 0 var(--space-1) 0',
                                    lineHeight: '1.1'
                                }}>₺{(kpiData.promissoryNotePrincipal || 0).toLocaleString('tr-TR')}</p>
                                <p style={{
                                    fontSize: 'var(--font-xs)',
                                    color: '#3b82f6',
                                    margin: 0
                                }}>kredi tutarı</p>
                            </div>

                            {/* Kar Dahil Geri Dönüş */}
                            <div style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                padding: 'var(--space-6)',
                                textAlign: 'center'
                            }}>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    fontWeight: '600',
                                    color: '#1e40af',
                                    margin: '0 0 var(--space-2) 0',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Kar Dahil Geri Dönüş</p>
                                <p style={{
                                    fontSize: 'var(--font-3xl)',
                                    fontWeight: '800',
                                    color: '#1e3a8a',
                                    margin: '0 0 var(--space-1) 0',
                                    lineHeight: '1.1'
                                }}>₺{(kpiData.promissoryNoteTotalReturn || 0).toLocaleString('tr-TR')}</p>
                                <p style={{
                                    fontSize: 'var(--font-xs)',
                                    color: '#3b82f6',
                                    margin: 0
                                }}>toplam geri ödeme</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Section - Using hook data */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                    gap: 'var(--space-6)',
                    marginBottom: 'var(--space-12)'
                }}>
                    {/* Purchase vs Sales Amount */}
                    <div className="card" style={{
                        background: 'var(--white)',
                        border: '1px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'var(--gray-50)',
                            padding: 'var(--space-5) var(--space-6)',
                            borderBottom: '1px solid var(--gray-200)'
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: 'var(--font-lg)',
                                    fontWeight: '700',
                                    margin: 0,
                                    color: 'var(--text-primary)'
                                }}>Alım vs Satış Tutarları</h3>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    color: 'var(--text-secondary)',
                                    margin: 'var(--space-1) 0 0 0'
                                }}>Aylık finansal performans</p>
                            </div>
                            <button
                                onClick={() => openModal('purchaseVsSales', chartData.purchaseVsSales)}
                                style={{
                                    background: 'none',
                                    border: '1px solid var(--gray-300)',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-lg)',
                                    padding: 'var(--space-2)',
                                    borderRadius: 'var(--radius)',
                                    transition: 'var(--transition)'
                                }}
                            >
                                <FaEye />
                            </button>
                        </div>
                        <div style={{ padding: 'var(--space-6)' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData.purchaseVsSales}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--white)',
                                            border: '1px solid var(--gray-200)',
                                            borderRadius: 'var(--radius)',
                                            boxShadow: 'var(--shadow)'
                                        }}
                                        formatter={(value, name) => {
                                            if (name === 'purchases') {
                                                return [`₺${value?.toLocaleString('tr-TR')}`, 'Alım Tutarı'];
                                            } else if (name === 'sales') {
                                                return [`₺${value?.toLocaleString('tr-TR')}`, 'Satış Tutarı'];
                                            }
                                            return [`₺${value?.toLocaleString('tr-TR')}`, name];
                                        }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <Bar dataKey="purchases" fill="#DC7272" name="Alım Tutarı" />
                                    <Bar dataKey="sales" fill="#65A572" name="Satış Tutarı" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Purchase vs Sales Count */}
                    <div className="card" style={{
                        background: 'var(--white)',
                        border: '1px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'var(--gray-50)',
                            padding: 'var(--space-5) var(--space-6)',
                            borderBottom: '1px solid var(--gray-200)'
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: 'var(--font-lg)',
                                    fontWeight: '700',
                                    margin: 0,
                                    color: 'var(--text-primary)'
                                }}>Alım vs Satış Adetleri</h3>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    color: 'var(--text-secondary)',
                                    margin: 'var(--space-1) 0 0 0'
                                }}>Aylık araç sayısı karşılaştırması</p>
                            </div>
                        </div>
                        <div style={{ padding: 'var(--space-6)' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData.purchaseVsSalesCount}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--white)',
                                            border: '1px solid var(--gray-200)',
                                            borderRadius: 'var(--radius)',
                                            boxShadow: 'var(--shadow)'
                                        }}
                                        formatter={(value, name) => {
                                            if (name === 'purchaseCount') {
                                                return [`${value} araç`, 'Alım Adedi'];
                                            } else if (name === 'salesCount') {
                                                return [`${value} araç`, 'Satış Adedi'];
                                            }
                                            return [`${value} araç`, name];
                                        }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <Line type="monotone" dataKey="purchaseCount" stroke="#DC7272" strokeWidth={3} name="Alım Adedi" />
                                    <Line type="monotone" dataKey="salesCount" stroke="#65A572" strokeWidth={3} name="Satış Adedi" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Sales Types Analysis */}
                    <div className="card" style={{
                        background: 'var(--white)',
                        border: '1px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'var(--gray-50)',
                            padding: 'var(--space-5) var(--space-6)',
                            borderBottom: '1px solid var(--gray-200)'
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: 'var(--font-lg)',
                                    fontWeight: '700',
                                    margin: 0,
                                    color: 'var(--text-primary)'
                                }}>Aylık Satış Türleri</h3>
                                <p style={{
                                    fontSize: 'var(--font-sm)',
                                    color: 'var(--text-secondary)',
                                    margin: 'var(--space-1) 0 0 0'
                                }}>Sözleşmeli vs Direkt satışlar</p>
                            </div>
                        </div>
                        <div style={{ padding: 'var(--space-6)' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData.monthlySalesData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                                        axisLine={{ stroke: 'var(--gray-300)' }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--white)',
                                            border: '1px solid var(--gray-200)',
                                            borderRadius: 'var(--radius)',
                                            boxShadow: 'var(--shadow)'
                                        }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <Line type="monotone" dataKey="senetli" stroke="#65A572" strokeWidth={3} name="Sözleşmeli" />
                                    <Line type="monotone" dataKey="pesin" stroke="#DC7272" strokeWidth={3} name="Direkt" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-4)' }}>
                    <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', maxWidth: '90vw', width: '1000px', maxHeight: '90vh', overflow: 'auto' }}>
                        <div style={{ padding: 'var(--space-6)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                                <h2 style={{ fontSize: 'var(--font-xl)', fontWeight: '700', margin: 0 }}>
                                    {modalType === 'galleryVsExternal' && 'Galeri vs Dışarıdan Satış Analizi'}
                                    {modalType === 'supplierAnalysis' && 'Tedarikçi Performans Analizi'}
                                    {modalType === 'purchaseVsSales' && 'Alım vs Satış Detayı'}
                                </h2>
                                <button
                                    onClick={closeModal}
                                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-lg)' }}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            {renderModalContent()}
                        </div>
                    </div>
                </div>
            )}

            {/* Senetli Araç Listesi Modal */}
            {showPromissoryNoteModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-4)' }}>
                    <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', maxWidth: '95vw', width: '1200px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {/* Modal Header */}
                        <div style={{ 
                            padding: 'var(--space-5) var(--space-6)', 
                            borderBottom: '1px solid var(--gray-200)',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center'
                        }}>
                            <div>
                                <h2 style={{ fontSize: 'var(--font-xl)', fontWeight: '700', margin: 0, color: 'white' }}>
                                    Senetli Araç Listesi
                                </h2>
                                <p style={{ fontSize: 'var(--font-sm)', color: 'rgba(255,255,255,0.8)', margin: 'var(--space-1) 0 0 0' }}>
                                    Seçilen tarih aralığındaki senetli satışlar
                                </p>
                            </div>
                            <button
                                onClick={closePromissoryNoteModal}
                                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 'var(--font-lg)', padding: 'var(--space-2)', borderRadius: 'var(--radius)' }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: 'var(--space-6)', overflow: 'auto', flex: 1 }}>
                            {promissoryNoteListLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Yükleniyor...</span>
                                    </div>
                                </div>
                            ) : promissoryNoteList.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-secondary)' }}>
                                    <FaFileContract style={{ fontSize: '48px', marginBottom: 'var(--space-4)', opacity: 0.5 }} />
                                    <p>Seçilen tarih aralığında senetli satış bulunamadı.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-sm)' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: '600', color: 'var(--text-primary)' }}>#</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: '600', color: 'var(--text-primary)' }}>Plaka</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: '600', color: 'var(--text-primary)' }}>Marka/Model</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: '600', color: 'var(--text-primary)' }}>Müşteri</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>Ana Para</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>Kar Dahil Geri Dönüş</th>
                                                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'center', fontWeight: '600', color: 'var(--text-primary)' }}>Satış Tarihi</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {promissoryNoteList.map((item, index) => (
                                                <tr key={item.plateNumber || index} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--text-secondary)' }}>{index + 1}</td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontWeight: '600', color: 'var(--text-primary)' }}>{item.plateNumber}</td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--text-primary)' }}>{item.brand} {item.model}</td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--text-primary)' }}>{item.customerName || '-'}</td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', color: 'var(--text-primary)' }}>
                                                        ₺{(item.loanAmount || 0).toLocaleString('tr-TR')}
                                                    </td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                                        ₺{(item.totalPayment || 0).toLocaleString('tr-TR')}
                                                    </td>
                                                    <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                                        {item.saleDate ? new Date(item.saleDate).toLocaleDateString('tr-TR') : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ background: 'var(--gray-50)', borderTop: '2px solid var(--gray-300)' }}>
                                                <td colSpan="4" style={{ padding: 'var(--space-3) var(--space-4)', fontWeight: '700', color: 'var(--text-primary)' }}>
                                                    TOPLAM ({promissoryNoteList.length} araç)
                                                </td>
                                                <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: '700', color: 'var(--text-primary)' }}>
                                                    ₺{promissoryNoteList.reduce((sum, item) => sum + (item.loanAmount || 0), 0).toLocaleString('tr-TR')}
                                                </td>
                                                <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: '700', color: '#059669' }}>
                                                    ₺{promissoryNoteList.reduce((sum, item) => sum + (item.totalPayment || 0), 0).toLocaleString('tr-TR')}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAnalyticsPage;
